import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import PaymentService from '../../../../service/pay/pay';

interface Price {
  plan_id: number;
  grade: string;
  duration_mon: number;
  plan_name: string;
  plan_prc: number;
  is_discount: string;
  region_type: string;
}

const months: { m: number; percent: string }[] = [
  {
    m: 1,
    percent: '',
  },
  {
    m: 3,
    percent: '20',
  },
  {
    m: 6,
    percent: '30',
  },
  {
    m: 12,
    percent: '40',
  },
];

function ComparePrice() {
  const [selectedMonth, setSelectedMonth] = useState(12);
  const [selectedRegion, setSelectedRegion] = useState('서울/수도권');
  const [priceData, setPriceData] = useState<Price[]>([]);

  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tabScrollRef = useRef<HTMLDivElement>(null);

  const getRegionOptions = (data: Price[]): string[] => {
    const set = new Set(data.map((p) => p.plan_name));
    set.delete('전국');
    return Array.from(set);
  };

  const regionOptions = useMemo(() => getRegionOptions(priceData), [priceData]);

  const REGION_LOCAL_DESC: Record<string, string> = {
    수도권: '서울, 경기, 인천',
    서울: '중앙/동/서/남/북부 지법 및 지원',
    '경기/인천': '의정부/수원/인천 지법 및 지원',
    충청권: '대전/청주 지법 및 지원',
    강원권: '춘천 지법 및 지원',
    경상권: '대구/부산/울산/창원 지법 및 지원',
    호남권: '광주/전주 지법 및 지원',
    제주도: '제주 지법 및 지원',
  };

  const formatPrice = (n?: number) =>
    n == null ? '' : new Intl.NumberFormat('ko-KR').format(n) + '원';

  /**
   * plan_name|grade|month|is_discount가 key인 맵
   */
  const priceMap = useMemo(() => {
    const m = new Map<string, number>();
    priceData.forEach((p) => {
      m.set(
        [p.plan_name, p.grade, p.duration_mon, p.is_discount].join('|'),
        p.plan_prc,
      );
    });
    return m;
  }, [priceData]);

  const getPrice = (
    plan: string,
    grade: 'Standard' | 'Premium',
    mon: number,
    isDisc: 'Y' | 'N',
  ) => priceMap.get([plan, grade, mon, isDisc].join('|'));

  // 할인 데이터 유무 체크
  const hasDiscountFor = useMemo(() => {
    const byPlan = new Map<string, boolean>();
    priceData.forEach((p) => {
      if (p.grade === 'Standard' && p.is_discount === 'Y') {
        byPlan.set(p.plan_name, true);
      }
    });
    return (plan: string) => !!byPlan.get(plan);
  }, [priceData]);

  const getMembershipTable = async () => {
    try {
      const res = await PaymentService.membershipTable();
      const { standard: std = [], premium: prm = [] } = res.data;
      setPriceData([...std, ...prm]);
    } catch (error) {
      alert('상품 목록을 불러오는 데 실패했습니다.');
    }
  };

  const parseRegion = (raw: string) => {
    const m = raw.match(/^(.*?)\s*\((.*?)\)\s*$/);
    return m
      ? { name: m[1].trim(), desc: m[2].trim() }
      : { name: raw, desc: undefined };
  };

  useEffect(() => {
    getMembershipTable();
  }, []);

  return (
    <Container>
      <Header>
        <Title>기간별 요금 비교</Title>
        <OptionSection>
          <Option>
            <OptionTitle>기간</OptionTitle>
            <TabBox ref={tabScrollRef}>
              <TabInner>
                {months.map(({ m, percent }) => (
                  <Tab
                    key={m}
                    $active={selectedMonth === m}
                    onClick={() => setSelectedMonth(m)}
                  >
                    {m}개월
                    {percent && (
                      <MonthPercent>
                        -{percent}%{m === 12 && '~'}
                      </MonthPercent>
                    )}
                  </Tab>
                ))}
              </TabInner>
            </TabBox>
          </Option>
        </OptionSection>
      </Header>

      <Content>
        <ContentHeader>
          <HeaderTitle>요금제</HeaderTitle>
          <MembershipBadge>스탠다드</MembershipBadge>
          {selectedMonth !== 1 && <MembershipBadge>프리미엄</MembershipBadge>}
        </ContentHeader>
        <TableRow>
          <Th>전국</Th>
          <Td $hasDiscount={hasDiscountFor('전국')}>
            {formatPrice(getPrice('전국', 'Standard', selectedMonth, 'N'))}
          </Td>
          {selectedMonth !== 1 && (
            <Td $hasDiscount={hasDiscountFor('전국')}>
              {formatPrice(getPrice('전국', 'Premium', selectedMonth, 'N'))}
            </Td>
          )}
        </TableRow>
        {hasDiscountFor('전국') && (
          <DiscountTableRow>
            <DiscountTh>전국 특별할인</DiscountTh>
            <DiscountTd>
              {formatPrice(getPrice('전국', 'Standard', selectedMonth, 'Y'))}
            </DiscountTd>
            {selectedMonth !== 1 && (
              <DiscountTd>
                {formatPrice(getPrice('전국', 'Premium', selectedMonth, 'Y'))}
              </DiscountTd>
            )}
          </DiscountTableRow>
        )}
        {regionOptions.map((region) => {
          const { name } = parseRegion(region);
          const desc = REGION_LOCAL_DESC[name];
          return (
            <TableRow key={region}>
              <Th>
                {name}
                {desc && (
                  <LocalDesc>
                    {desc} {region !== '제주도' && '+제주'}
                  </LocalDesc>
                )}
              </Th>
              <Td>
                {formatPrice(getPrice(region, 'Standard', selectedMonth, 'N'))}
              </Td>
              {selectedMonth !== 1 && <Td>{''}</Td>}
            </TableRow>
          );
        })}
      </Content>
    </Container>
  );
}

export default ComparePrice;

const Container = styled.div`
  display: flex;
  padding: 40px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Title = styled.div`
  align-self: stretch;
  color: #222;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.48px;
`;

const OptionSection = styled.div`
  display: flex;
  padding: 16px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  border-bottom: 1px solid #e1e2e4;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const OptionTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const TabBox = styled.div`
  width: 100%;
  max-width: inherit;
  background-color: #fff;
  overflow-x: auto;

  /* 스크롤바 숨기기 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE, Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }
`;

const TabInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  &::after {
    content: '';
    flex: 0 0 10px;
  }
`;

const Tab = styled.div<{ $active?: boolean }>`
  display: flex;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 20px;

  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;

  color: ${({ $active }) => ($active ? '#ffffff' : '#989BA2')};
  background-color: ${({ $active }) => ($active ? '#222222' : '#FFFFFF')};
  border: 1px solid;
  border-color: ${({ $active }) => ($active ? 'transparent' : ' #E5E8EA')};
  white-space: nowrap;

  & > span {
    color: ${({ $active }) => ($active ? '#C7C4FF' : '#4228D6')};
    font-weight: 700;
  }
`;

const MonthPercent = styled.span``;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const HeaderTitle = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;

  color: #222;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const MembershipBadge = styled.div`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  width: 100px;

  color: #222;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const TableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const Th = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;

  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Td = styled.div<{ $hasDiscount?: boolean }>`
  display: flex;
  width: 100px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
  text-decoration: ${({ $hasDiscount }) => $hasDiscount && 'line-through'};
  color: ${({ $hasDiscount }) => ($hasDiscount ? '#616161' : '#222')};
`;

const DiscountTableRow = styled(TableRow)`
  background: #f2f2ff;
  border-radius: 8px;
`;

const DiscountTh = styled(Th)`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  font-weight: 700;
  color: #4228d6;
`;

const DiscountTd = styled(Td)`
  font-weight: 700;
  color: #4228d6;
`;

const DiscountChip = styled.div`
  display: flex;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  background: #1f0a8a;

  color: #fff;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.26px;
`;

const LocalDesc = styled.div`
  color: #616161;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;
`;
